import { Fragment, useRef } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * FORMATTED MESSAGES, WITHOUT A RICH TEXT EDITOR.
 *
 * People want bold, links and lists in a comment. The obvious answer -
 * contentEditable producing HTML - means storing HTML somebody typed and
 * rendering it back, which is an XSS hole held shut by a sanitiser that has to
 * be right every time. So what is stored is a small, fixed subset of Markdown,
 * and it is rendered into React ELEMENTS. Nothing ever reaches
 * dangerouslySetInnerHTML, so there is no hole to hold shut.
 *
 * The second reason is the email. A message written here goes out as both
 * text/plain and text/html; Markdown is already the readable plain-text form,
 * so the two parts say the same thing without a converter guessing at one of
 * them.
 *
 * The subset, deliberately small:
 *   **bold**   *italic*   `code`   [label](url)   bare links
 *   - list     > quote
 */

const INLINE =
  /(\*\*[^*\n]+\*\*|\*[^*\n]+\*|`[^`\n]+`|\[[^\]\n]+\]\((?:https?:\/\/|mailto:)[^)\s]+\)|(?:https?:\/\/|www\.)[^\s<)]+)/g;

const safeHref = (url) => {
  const href = url.startsWith('www.') ? `https://${url}` : url;
  // Only these two schemes are ever turned into a link. javascript: and data:
  // are the reason this check exists.
  return /^(https?:\/\/|mailto:)/i.test(href) ? href : null;
};

const inline = (text, keyPrefix) =>
  text
    .split(INLINE)
    .filter((part) => part !== undefined && part !== '')
    .map((part, index) => {
      const key = `${keyPrefix}-${index}`;

      if (/^\*\*[^*]+\*\*$/.test(part)) return <strong key={key}>{part.slice(2, -2)}</strong>;
      if (/^\*[^*]+\*$/.test(part)) return <em key={key}>{part.slice(1, -1)}</em>;
      if (/^`[^`]+`$/.test(part)) return <code key={key}>{part.slice(1, -1)}</code>;

      const link = part.match(/^\[([^\]]+)\]\((.+)\)$/);
      if (link) {
        const href = safeHref(link[2]);
        return href ? (
          <a key={key} href={href} target="_blank" rel="noopener noreferrer">
            {link[1]}
          </a>
        ) : (
          <Fragment key={key}>{part}</Fragment>
        );
      }

      if (/^(https?:\/\/|www\.)/.test(part)) {
        const href = safeHref(part);
        return href ? (
          <a key={key} href={href} target="_blank" rel="noopener noreferrer">
            {part}
          </a>
        ) : (
          <Fragment key={key}>{part}</Fragment>
        );
      }

      return <Fragment key={key}>{part}</Fragment>;
    });

/** Renders the stored text. Never returns HTML - only React elements. */
export const RichText = ({ text, className = 'rich' }) => {
  if (!text?.trim()) return null;

  const blocks = [];
  let list = null;
  let quote = null;
  let paragraph = [];

  const flush = () => {
    if (list) {
      blocks.push(
        <ul key={`b${blocks.length}`}>
          {list.map((item, i) => (
            <li key={i}>{inline(item, `l${blocks.length}-${i}`)}</li>
          ))}
        </ul>,
      );
      list = null;
    }
    if (quote) {
      blocks.push(
        <blockquote key={`b${blocks.length}`}>{inline(quote.join('\n'), `q${blocks.length}`)}</blockquote>,
      );
      quote = null;
    }
    if (paragraph.length) {
      const index = blocks.length;
      blocks.push(
        <p key={`b${index}`}>
          {paragraph.map((line, i) => (
            <Fragment key={i}>
              {i > 0 ? <br /> : null}
              {inline(line, `p${index}-${i}`)}
            </Fragment>
          ))}
        </p>,
      );
      paragraph = [];
    }
  };

  for (const raw of text.split('\n')) {
    const line = raw.trimEnd();
    if (!line.trim()) {
      flush();
    } else if (/^[-*]\s+/.test(line)) {
      if (!list) flush();
      list = [...(list ?? []), line.replace(/^[-*]\s+/, '')];
    } else if (/^>\s?/.test(line)) {
      if (!quote) flush();
      quote = [...(quote ?? []), line.replace(/^>\s?/, '')];
    } else {
      if (list || quote) flush();
      paragraph.push(line);
    }
  }
  flush();

  return <div className={className}>{blocks}</div>;
};

/**
 * The toolbar.
 *
 * It wraps the selection in the textarea it is given, rather than owning one:
 * the comment box already has its own with the "@" mention list in it, and two
 * competing text areas would be worse than no toolbar at all.
 */
export const RichTextToolbar = ({ inputRef, value, onChange, disabled = false }) => {
  const { t } = useTranslation();

  const apply = (transform) => {
    const field = inputRef?.current;
    if (!field) return;

    const start = field.selectionStart ?? value.length;
    const end = field.selectionEnd ?? value.length;
    const selected = value.slice(start, end);
    const { text, from, to } = transform(selected, value.slice(0, start), value.slice(end));

    onChange(text);
    // Put the caret back where the writer expects it, after React re-renders.
    requestAnimationFrame(() => {
      field.focus();
      field.setSelectionRange(from, to);
    });
  };

  const wrap = (marker, placeholderKey) => () =>
    apply((selected, before, after) => {
      const body = selected || t(placeholderKey);
      return {
        text: `${before}${marker}${body}${marker}${after}`,
        from: before.length + marker.length,
        to: before.length + marker.length + body.length,
      };
    });

  const prefixLines = (marker) => () =>
    apply((selected, before, after) => {
      const body = (selected || t('rich.item'))
        .split('\n')
        .map((line) => (line.startsWith(marker) ? line : marker + line))
        .join('\n');
      // Start on a line of its own, or the marker is just text mid-sentence.
      const lead = before && !before.endsWith('\n') ? '\n' : '';
      return {
        text: `${before}${lead}${body}${after}`,
        from: before.length + lead.length + marker.length,
        to: before.length + lead.length + body.length,
      };
    });

  const link = () =>
    apply((selected, before, after) => {
      // Selected a URL? Then it is the target, and the label is what to type.
      const isUrl = /^(https?:\/\/|www\.)/i.test(selected.trim());
      const label = isUrl ? t('rich.linkLabel') : selected || t('rich.linkLabel');
      const url = isUrl ? selected.trim() : 'https://';
      const text = `${before}[${label}](${url})${after}`;
      // Select the half the writer still has to fill in.
      const from = isUrl ? before.length + 1 : before.length + label.length + 3;
      const to = isUrl ? from + label.length : from + url.length;
      return { text, from, to };
    });

  /**
   * B and I read as themselves in any font. The other three were emoji, which
   * came out as empty boxes wherever the font had no glyph - so they are the
   * translated word instead, which cannot fail to render.
   */
  const buttons = [
    ['rich.bold', 'B', wrap('**', 'rich.boldText'), 'rich-toolbar__btn rich-toolbar__btn--bold'],
    ['rich.italic', 'I', wrap('*', 'rich.italicText'), 'rich-toolbar__btn rich-toolbar__btn--italic'],
    ['rich.link', t('rich.link'), link, 'rich-toolbar__btn rich-toolbar__btn--word'],
    ['rich.list', t('rich.list'), prefixLines('- '), 'rich-toolbar__btn rich-toolbar__btn--word'],
    ['rich.quote', t('rich.quote'), prefixLines('> '), 'rich-toolbar__btn rich-toolbar__btn--word'],
  ];

  return (
    <div className="rich-toolbar">
      {buttons.map(([key, glyph, handler, className]) => (
        <button
          key={key}
          type="button"
          className={className}
          onClick={handler}
          disabled={disabled}
          title={t(key)}
          aria-label={t(key)}
        >
          {glyph}
        </button>
      ))}
      <span className="rich-toolbar__hint">{t('rich.hint')}</span>
    </div>
  );
};

export default RichText;
